import {
  DuplicateCandidate,
  DuplicateAction,
  summarizeCandidate,
  availableActions,
  dialogSubtitle,
} from '../utils/duplicateChoice';
import { ChoiceRow, Dialog } from './ui';

/**
 * The "you already have one of these" prompt (backlog A5).
 *
 * PRESENTATIONAL ONLY. Every decision - which actions exist for a candidate, and how a
 * candidate reads as one line - comes from `utils/duplicateChoice.ts` and none of it is
 * reimplemented here. That split is not stylistic: `frontend/vitest.config.ts` is
 * `environment: 'node'` with `include: ['src/**\/*.test.ts']` and there is no jsdom or
 * testing-library, so a `.test.tsx` is never even COLLECTED (backlog B14). Anything that
 * lives in this file is unverifiable by any test in this repo, so nothing that can be wrong
 * in an interesting way is allowed to live here.
 */

const ACTION_LABEL: Record<DuplicateAction, string> = {
  open: 'Open existing',
  attach: 'Attach here',
  create_separate: 'Create separate',
};

/**
 * `create_separate` is deliberately the quietest button on the row. It is the only action
 * that ends with two rows for one building - the exact outcome A5 exists to prevent - so it
 * is offered, never encouraged.
 *
 * `open` and `attach` are BOTH primary, and that still leaves the one primary per view that
 * `Button` expects (see MasterPropertyDetail) because `availableActions` never returns the two
 * together: a document present means attach, absent means open. That mutual exclusivity is an
 * invariant of `availableActions` and is asserted in `duplicateChoice.test.ts` - this file
 * cannot be tested (B14), so nothing here may depend on an assumption that lives only here.
 *
 * Attach earning primary on the upload path is the correction, not a style tweak. It reaches
 * the same property "open" would AND re-homes a document that is already uploaded and already
 * extracted; the loud button used to be the one that stranded it.
 */
const ACTION_TONE: Record<DuplicateAction, 'main' | 'plain'> = {
  open: 'main',
  attach: 'main',
  create_separate: 'plain',
};

interface Props {
  candidates: DuplicateCandidate[];
  /** Present only on the upload path. Its absence removes "Attach here"; its PRESENCE removes
   *  "Open existing", which would strand the uploaded document. It also selects the subtitle,
   *  since "nothing has been saved yet" is only true without it. */
  documentId?: string;
  /** Disables every button while the chosen action is in flight - a second click on
   *  "Create separate" would create a second duplicate. */
  busy?: boolean;
  onChoose: (action: DuplicateAction, candidate: DuplicateCandidate) => void;
  onCancel: () => void;
}

export default function DuplicatePropertyDialog({
  candidates,
  documentId,
  busy = false,
  onChoose,
  onCancel,
}: Props) {
  return (
    /* OO5d: through the Dialog primitive. ⛔ z-60, NOT the default z-50: the Dashboard's
       "Add Property" modal is z-50 and stays open BEHIND this one, so a cancel returns the user
       to the form they already filled in. That is exactly why Dialog takes the layer as a prop. */
    <Dialog
      open
      onClose={onCancel}
      title={`You already have ${candidates.length === 1 ? 'a property' : 'properties'} like this`}
      size="md"
      zClassName="z-[60]"
      dismissible={!busy}
      /* UI-GLASS1 (the 132nd): a short list of choices, so it is drawn as iOS 26's menu - a frosted panel, pill rows.
         ⛔ The LOOK only: which actions exist is still `availableActions`, untouched. */
      look="choices"
      footer={<ChoiceRow label="Cancel" tone="quiet" onClick={onCancel} disabled={busy} />}
    >
          {/* Which sentence is true depends on the path this was opened from, so the choice
              lives in the tested util rather than as a literal here. */}
          <p className="text-sm text-gray-600">{dialogSubtitle({ documentId })}</p>

          <ul className="mt-4 space-y-3">
            {candidates.map((c) => (
              <li
                key={c.id}
                className={`rounded-[22px] p-3 ${c.is_archived ? 'bg-white/35' : 'bg-white/55'}`}
              >
                <p className={`text-sm ${c.is_archived ? 'text-gray-500' : 'text-gray-900'}`}>
                  {summarizeCandidate(c)}
                </p>

                {/* An archived property is invisible in My Deals, so choosing one lands the
                    user somewhere they cannot navigate back to. Say so rather than hiding the
                    candidate - a duplicate they cannot see is still a duplicate.

                    "until it is restored" was removed because this app cannot restore one:
                    `is_archived: false` is never written anywhere in the repo, and there is no
                    unarchive route, service or UI. Archiving is one-way (propertyController's
                    soft delete sets it true), so the old wording named an action the user
                    would have gone looking for and never found. */}
                {c.is_archived && (
                  <p className="mt-1 text-xs text-warning">
                    Archived - it does not appear in My Deals.
                  </p>
                )}

                <div className="mt-2 space-y-2">
                  {availableActions(c, { documentId }).map((action) => (
                    <ChoiceRow
                      key={action}
                      label={ACTION_LABEL[action]}
                      tone={ACTION_TONE[action]}
                      disabled={busy}
                      onClick={() => onChoose(action, c)}
                    />
                  ))}
                </div>
              </li>
            ))}
          </ul>

    </Dialog>
  );
}
